import { defineStore } from 'pinia';

export const useLocationStore = defineStore('location', {
    
    state: () => ({
        coords: null as { latitude: number; longitude: number; accuracy: number } | null,
        error: null as string | null,
        lastUpdated: null as Date | null,
        enabled: false as boolean,
        watchId: null as number | null,
        // Bumped by the map's Locate Me control. GlobalLocation owns the only
        // geolocation calls in the app, so a component that wants a fresh fix
        // asks for one through here rather than calling the browser itself.
        requestNonce: 0 as number,
        // True only once the browser has actually refused. Locate Me cannot
        // re-prompt past a denial — that is a browser-level setting — so the
        // control needs to say so instead of appearing to do nothing.
        denied: false as boolean,
    }),
    
    getters: {
        // Computed location properties for easier access
        location: (state) => {
            // Return the center of chicago if no location is set
            if (!state.coords) return {
                latitude: 41.8781,
                longitude: -87.6298,
                accuracy: 10000,
            };
            return {
                latitude: state.coords.latitude,
                longitude: state.coords.longitude,
                accuracy: state.coords.accuracy,
            };
        },
        hasLocation: (state) => !!state.coords,
        hasPermission: (state) => !!state.enabled,
        watching: (state) => !!state.watchId,
    },
    
    actions: {
        setCoords(coords: { latitude: number; longitude: number; accuracy: number } | null) {
            this.coords = coords;
            this.lastUpdated = coords ? new Date() : null;
            if (coords) localStorage.setItem('userLocation', JSON.stringify(coords));
        },
        
        setError(error: string | null) {
            this.error = error;
        },

        // Ask GlobalLocation for a fresh fix. Deliberately a counter and not a
        // boolean: pressing Locate Me twice has to fire twice.
        requestFix() {
            this.requestNonce++;
        },

        setDenied(denied: boolean) {
            this.denied = denied;
            this.error = denied ? 'Location is turned off for this site.' : null;
        },
        
        clearLocation() {
            this.coords = null;
            this.error = null;
            this.lastUpdated = null;
            localStorage.removeItem('userLocation');
        },
        
        // Calculate distance between stored location and a coordinate
        calculateDistance(lat2: number, lon2: number): number | null {
            if (!this.coords) return null;
            const R = 3959; // Earth's radius in miles
            const lat1 = this.coords.latitude;
            const lon1 = this.coords.longitude;
            const dLat = ((lat2 - lat1) * Math.PI) / 180;
            const dLon = ((lon2 - lon1) * Math.PI) / 180;
            const a =
                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos((lat1 * Math.PI) / 180) * 
                Math.cos((lat2 * Math.PI) / 180) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
                
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            return R * c;
        },
        
        // Load cached location on init
        loadCachedLocation() {
            const cached = localStorage.getItem('userLocation');
            if (cached) {
                try {
                    this.coords = JSON.parse(cached);
                    console.log('Loaded cached location:', this.coords);
                } catch (e) {
                    console.error('Failed to parse cached location:', e);
                }
            }
        },
    },
});